import React from 'react';
import {
    Box,
    Collapse,
    Editable,
    EditableInput,
    EditablePreview,
    Flex,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    TableColumnHeaderProps,
    Tag,
    Td,
    Text,
    Th,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import { TbArrowBackUp, TbArrowDown, TbArrowUp, TbBulb, TbChevronDown, TbChevronRight, TbCopy, TbFlag, TbDotsVertical, TbInfoCircle, TbTrash, TbUnlink } from 'react-icons/tb';
import type {
    LkqListPriceDiscountInfo,
    LkqListPriceMarkupInfo,
    SubletListPriceDiscountInfo,
    SubletListPriceMarkupInfo,
} from '../../types/invoice';
import { formatSubletListPriceDiscountTooltip } from '../../utils/subletListPriceDiscountTooltip';
import { formatSubletListPriceTooltip } from '../../utils/subletListPriceTooltip';
import { formatLkqListPriceDiscountTooltip } from '../../utils/lkqListPriceDiscountTooltip';
import { formatLkqListPriceTooltip } from '../../utils/lkqListPriceTooltip';
import { getLineStatusDisplayText, getLineStatusTagColor } from '../../utils/lineStatusTag';

export const INVOICE_TAB_ACCENT = '#0071E3';
export const INVOICE_TAB_ACCENT_HOVER = '#3544a0';
export const VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON =
    'The supplier on this invoice is not matched to a NEX vendor yet. Resolve the vendor in Key Identifiers (and save if prompted), then try again.';

/** Icon row in the 116px actions column — aligns chevrons, unmatch, flags, etc. */
export const invoicePartTableActionsIconStackProps = {
    spacing: 0.5,
    align: 'center' as const,
    justify: 'flex-start' as const,
    pl: 5,
    flexWrap: 'nowrap' as const,
};

/** Matches invoice grid actions column; tinted blue so the matched-NEX strip extends across the full row. */
export const invoiceMatchedNexActionsColumnFlexProps = {
    w: '116px',
    minW: '116px',
    px: 2,
    bg: 'blue.50',
    direction: 'column' as const,
    alignSelf: 'stretch' as const,
    align: 'flex-start' as const,
    justify: 'center' as const,
    flexShrink: 0 as const,
};

/** Kill horizontal rule between matched NEX title row and adjacent rows (`tbody tr + tr td` gridline). */
export const invoiceMatchedNexCollapseHeaderTdSx = {
    borderTopWidth: '0 !important',
    borderTop: 'none !important',
    borderBottomWidth: '0 !important',
    borderBottom: 'none !important',
} as const;

export const invoiceMatchedNexDetailDataRowTrSx = {
    '& > td': {
        borderTopWidth: '0 !important',
        borderTop: 'none !important',
    },
} as const;

/** Extra space after expanded matched NEX block; no top rule against NEX data row (`tbody tr + tr td`). */
export const invoiceMatchedNexAfterBlockSpacerTdSx = {
    borderTop: 'none !important',
    borderTopWidth: '0 !important',
} as const;

export function InvoiceMatchedNexAfterBlockSpacerRow() {
    return (
        <Tr>
            <Td
                colSpan={7}
                border="none"
                bg="white"
                py={3}
                px={0}
                sx={invoiceMatchedNexAfterBlockSpacerTdSx}
            />
        </Tr>
    );
}

export function getInvoiceItemsTableSx(showGridlines = true) {
    return {
        tableLayout: 'fixed' as const,
        borderCollapse: 'separate' as const,
        borderSpacing: 0,
        'thead tr:first-of-type th': { borderTop: 'none' },
        'thead tr:first-of-type th:first-of-type': { borderLeft: 'none', borderTopLeftRadius: '5px' },
        'thead tr:first-of-type th:last-of-type': { borderRight: 'none', borderTopRightRadius: '5px' },
        'tbody td:first-of-type': { borderLeft: 'none' },
        'tbody td:last-of-type': { borderRight: 'none' },
        'tbody tr:last-child td': { borderBottom: 'none' },
        'tbody tr:last-child td:first-of-type': { borderBottomLeftRadius: '5px' },
        'tbody tr:last-child td:last-of-type': { borderBottomRightRadius: '5px' },
        // Qty + money columns (4–7): Excel-style right alignment + tabular figures for digit alignment.
        'thead tr th:nth-of-type(4), thead tr th:nth-of-type(5), thead tr th:nth-of-type(6), thead tr th:nth-of-type(7)': {
            fontVariantNumeric: 'tabular-nums',
        },
        'tbody td:nth-of-type(4), tbody td:nth-of-type(5), tbody td:nth-of-type(6), tbody td:nth-of-type(7)': {
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
        },
        ...(showGridlines
            ? {
                  // Spreadsheet-like internal gridlines: both vertical and horizontal separators.
                  'thead th + th, tbody td + td': {
                      borderLeft: '1px solid',
                      borderLeftColor: 'gray.200',
                  },
                  'tbody tr + tr td': {
                      borderTop: '1px solid',
                      borderTopColor: 'gray.100',
                  },
              }
            : {
                  'thead th, tbody td': { borderColor: 'transparent !important' },
              }),
    };
}

export const invoiceEditableInputProps = {
    border: 'none' as const,
    outline: '1.5px solid' as const,
    outlineColor: 'blue.300',
    borderRadius: 'md',
    color: 'gray.900',
    bg: 'blue.50',
    py: '2px',
    px: 1,
    h: 'auto' as const,
    lineHeight: 'short' as const,
};

export const invoiceEditableInputFocusProps = {
    outline: '1.5px solid' as const,
    outlineColor: INVOICE_TAB_ACCENT,
    boxShadow: 'none',
};

/** Chakra props for `LineItemGridLandingEditable` / grid `EditableInput` (matches invoice numeric edit styling). */
export const invoicePartGridLandingEditableInputProps = {
    ...invoiceEditableInputProps,
    _focus: invoiceEditableInputFocusProps,
};

export function invoicePartGridEditableCaretAtEndOnFocus(e: React.FocusEvent<HTMLInputElement>) {
    window.setTimeout(() => e.target.setSelectionRange(e.target.value.length, e.target.value.length), 0);
}

/** Merged Chakra input props for `LineItemGridLandingEditable` (caret at end on focus). */
export function invoicePartGridLandingInputPropsWithCaretAtEnd() {
    return {
        ...invoicePartGridLandingEditableInputProps,
        onFocus: invoicePartGridEditableCaretAtEndOnFocus,
    };
}

/** Bordered focusable wrapper for invoice line/freight/misc/ignored paste grids. */
export function InvoicePartGridTableContainer({
    containerRef,
    isReadOnly,
    onMouseDownCapture,
    onPaste,
    children,
}: {
    containerRef: React.RefObject<HTMLDivElement | null>;
    isReadOnly?: boolean;
    onMouseDownCapture: (e: React.MouseEvent) => void;
    onPaste: (e: React.ClipboardEvent) => void;
    children: React.ReactNode;
}) {
    return (
        <Box
            ref={containerRef as React.LegacyRef<HTMLDivElement>}
            tabIndex={isReadOnly ? undefined : -1}
            borderTop="1px solid"
            borderTopColor="gray.200"
            borderLeft="none"
            borderRight="none"
            borderBottom="none"
            borderRadius="0"
            overflow="visible"
            outline="none"
            onMouseDownCapture={onMouseDownCapture}
            onPaste={onPaste}
            title={undefined}
        >
            {children}
        </Box>
    );
}

export function blurEditableOnEnter(e: React.KeyboardEvent) {
    if (e.key === 'Enter') setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 0);
}

interface InvoiceStandardHeaderRowProps {
    firstColumnContent?: React.ReactNode;
    qtyHeaderContent?: React.ReactNode;
    listPriceHeaderContent?: React.ReactNode;
}

const headerCellProps: TableColumnHeaderProps = {
    py: 3,
    color: 'gray.500',
    borderColor: 'gray.200',
    fontSize: 'xs',
    bg: 'gray.50',
};

/** Expand/collapse all matched NEX detail rows — same control in Line Items, Freight, Misc, and Ignored grids. */
export function InvoiceMatchedNexExpandAllHeaderControl({
    isVisible,
    allExpanded,
    onToggle,
}: {
    isVisible: boolean;
    allExpanded: boolean;
    onToggle: () => void;
}) {
    if (!isVisible) return null;
    return (
        <Flex justify="flex-start" pl={5} alignItems="center" minH="24px">
            <Tooltip label={allExpanded ? 'Collapse all matched NEX lines' : 'Expand all matched NEX lines'} placement="top" hasArrow>
                <IconButton
                    aria-label={allExpanded ? 'Collapse all matched NEX lines' : 'Expand all matched NEX lines'}
                    icon={<Icon as={(allExpanded ? TbChevronDown : TbChevronRight) as React.ElementType} boxSize={4} />}
                    size="xs"
                    variant="ghost"
                    color="blue.600"
                    _hover={{ bg: 'blue.50' }}
                    minW="auto"
                    h="auto"
                    p={0}
                    tabIndex={-1}
                    onClick={onToggle}
                />
            </Tooltip>
        </Flex>
    );
}

export function InvoiceSubletListPriceHeader({
    markup,
    discount,
}: {
    markup?: SubletListPriceMarkupInfo | null;
    discount?: SubletListPriceDiscountInfo | null;
}) {
    const tooltip = discount
        ? formatSubletListPriceDiscountTooltip(discount)
        : formatSubletListPriceTooltip(markup);
    return (
        <HStack spacing={1} justify="flex-end" align="center">
            <Text as="span" whiteSpace="nowrap">
                List Price
            </Text>
            <Tooltip
                label={tooltip}
                hasArrow
                placement="top"
                openDelay={200}
                whiteSpace="pre-line"
                maxW="360px"
                fontSize="xs"
            >
                <IconButton
                    aria-label={
                        discount
                            ? 'How sublet list and buy prices are calculated with discount'
                            : 'How sublet list price is calculated'
                    }
                    icon={<Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />}
                    variant="ghost"
                    size="xs"
                    minW="auto"
                    h="auto"
                    p={0}
                    color="gray.400"
                    _hover={{ color: 'gray.600', bg: 'transparent' }}
                    flexShrink={0}
                />
            </Tooltip>
        </HStack>
    );
}

export function InvoiceLkqListPriceHeader({
    markup,
    discount,
}: {
    markup?: LkqListPriceMarkupInfo | null;
    discount?: LkqListPriceDiscountInfo | null;
}) {
    const tooltip = discount
        ? formatLkqListPriceDiscountTooltip(discount)
        : formatLkqListPriceTooltip(markup);
    return (
        <HStack spacing={1} justify="flex-end" align="center">
            <Text as="span" whiteSpace="nowrap">
                List Price
            </Text>
            <Tooltip
                label={tooltip}
                hasArrow
                placement="top"
                openDelay={200}
                whiteSpace="pre-line"
                maxW="360px"
                fontSize="xs"
            >
                <IconButton
                    aria-label={
                        discount
                            ? 'How parts-specific list and buy prices are calculated with discount'
                            : 'How parts-specific list price is calculated'
                    }
                    icon={<Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />}
                    variant="ghost"
                    size="xs"
                    minW="auto"
                    h="auto"
                    p={0}
                    color="gray.400"
                    _hover={{ color: 'gray.600', bg: 'transparent' }}
                    flexShrink={0}
                />
            </Tooltip>
        </HStack>
    );
}

export function buildSubletListPriceHeaderContent(
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
    markup?: SubletListPriceMarkupInfo | null,
): React.ReactNode | undefined {
    if (invoiceType !== 'sublet') return undefined;
    return <InvoiceSubletListPriceHeader markup={markup} />;
}

export function buildListPriceHeaderContent(args: {
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet';
    subletListPriceMarkup?: SubletListPriceMarkupInfo | null;
    subletListPriceDiscount?: SubletListPriceDiscountInfo | null;
    lkqListPriceMarkup?: LkqListPriceMarkupInfo | null;
    lkqListPriceDiscount?: LkqListPriceDiscountInfo | null;
}): React.ReactNode | undefined {
    if (args.invoiceType === 'sublet') {
        return (
            <InvoiceSubletListPriceHeader
                markup={args.subletListPriceMarkup}
                discount={args.subletListPriceDiscount}
            />
        );
    }
    if (args.invoiceType === 'received' && (args.lkqListPriceDiscount || args.lkqListPriceMarkup)) {
        return (
            <InvoiceLkqListPriceHeader
                markup={args.lkqListPriceMarkup}
                discount={args.lkqListPriceDiscount}
            />
        );
    }
    return undefined;
}

export function InvoiceStandardHeaderRow({
    firstColumnContent,
    qtyHeaderContent = 'Qty',
    listPriceHeaderContent = 'List Price',
}: InvoiceStandardHeaderRowProps) {
    return (
        <Tr>
            <Th py={3} w="116px" minW="116px" borderColor="gray.200" bg="gray.50" px={2} verticalAlign="middle">
                {firstColumnContent}
            </Th>
            <Th {...headerCellProps} w="28%">
                Description
            </Th>
            <Th {...headerCellProps} w="22%" minW="120px" textAlign="left">
                Part #
            </Th>
            <Th {...headerCellProps} textAlign="right" w="8%" whiteSpace="nowrap">
                {qtyHeaderContent}
            </Th>
            <Th {...headerCellProps} textAlign="right" w="14%" minW="90px">
                {listPriceHeaderContent}
            </Th>
            <Th {...headerCellProps} textAlign="right" w="14%" minW="90px">
                Buy Price
            </Th>
            <Th {...headerCellProps} textAlign="right" w="12%" minW="90px">
                Total
            </Th>
        </Tr>
    );
}

interface InvoiceEditableFieldProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isReadOnly?: boolean;
    textAlign?: 'left' | 'right';
    previewWhiteSpace?: string;
    inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
    pattern?: string;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export function InvoiceEditableField({
    value,
    onChange,
    placeholder,
    isReadOnly,
    textAlign = 'left',
    previewWhiteSpace,
    inputMode,
    pattern,
    onFocus,
}: InvoiceEditableFieldProps) {
    return (
        <Editable value={value} onChange={onChange} placeholder={placeholder} isDisabled={!!isReadOnly}>
            <EditablePreview w="100%" cursor="pointer" color="gray.900" py={0} px={0} textAlign={textAlign} whiteSpace={previewWhiteSpace} />
            <EditableInput
                {...invoiceEditableInputProps}
                _focus={invoiceEditableInputFocusProps}
                onKeyDown={blurEditableOnEnter}
                onFocus={onFocus}
                textAlign={textAlign}
                inputMode={inputMode}
                pattern={pattern}
                type={inputMode === 'numeric' ? 'text' : undefined}
            />
        </Editable>
    );
}

interface InvoiceRowActionsMenuProps {
    isReadOnly?: boolean;
    onAddAbove?: () => void;
    onAdd: () => void;
    onDuplicate?: () => void;
    onDelete: () => void;
    onRestore?: () => void;
    /** Optional trigger icon (defaults to vertical dots — same as line items / freight / misc / ignored grids). */
    menuTriggerIcon?: React.ElementType;
}

export function InvoiceRowActionsMenu({
    isReadOnly,
    onAddAbove,
    onAdd,
    onDuplicate,
    onDelete,
    onRestore,
    menuTriggerIcon = TbDotsVertical as React.ElementType,
}: InvoiceRowActionsMenuProps) {
    const addAboveIcon = <Icon as={TbArrowUp as React.ElementType} boxSize={4} />;
    const addBelowIcon = <Icon as={TbArrowDown as React.ElementType} boxSize={4} />;

    /** Keep menu out of tab panels / overflow:auto columns so it is not clipped and does not flip over the tab bar. */
    const menuBoundary = typeof document !== 'undefined' ? document.body : undefined;

    return (
        <Menu
            strategy="fixed"
            placement="bottom-start"
            gutter={6}
            flip={false}
            preventOverflow
            boundary={menuBoundary}
            closeOnBlur
            autoSelect={false}
            eventListeners={{ scroll: true, resize: true }}
        >
            <MenuButton
                as={IconButton}
                aria-label="Row actions"
                icon={<Icon as={menuTriggerIcon as React.ElementType} boxSize={4} />}
                size="xs"
                variant="ghost"
                color="gray.400"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ color: 'gray.600', bg: 'gray.50', borderColor: 'gray.300' }}
                minW="auto"
                h="auto"
                p={1}
                borderRadius="md"
                boxShadow="none"
                isDisabled={!!isReadOnly}
                tabIndex={-1}
            />
            <Portal>
                <MenuList minW="180px" py={1} boxShadow="none" zIndex={2000}>
                    {onAddAbove ? (
                        <MenuItem
                            icon={addAboveIcon}
                            fontSize="sm"
                            onClick={onAddAbove}
                            isDisabled={!!isReadOnly}
                        >
                            Add row above
                        </MenuItem>
                    ) : null}
                    <MenuItem
                        icon={addBelowIcon}
                        fontSize="sm"
                        onClick={onAdd}
                        isDisabled={!!isReadOnly}
                    >
                        Add row below
                    </MenuItem>
                    {onDuplicate ? (
                        <MenuItem
                            icon={<Icon as={TbCopy as React.ElementType} boxSize={4} />}
                            fontSize="sm"
                            onClick={onDuplicate}
                            isDisabled={!!isReadOnly}
                        >
                            Duplicate row
                        </MenuItem>
                    ) : null}
                    <MenuItem
                        icon={<Icon as={TbTrash as React.ElementType} boxSize={4} color="red.500" />}
                        fontSize="sm"
                        color="red.500"
                        onClick={onDelete}
                        isDisabled={!!isReadOnly}
                    >
                        Delete row
                    </MenuItem>
                    {onRestore ? (
                        <MenuItem
                            icon={<Icon as={TbArrowBackUp as React.ElementType} boxSize={4} />}
                            fontSize="sm"
                            onClick={onRestore}
                            isDisabled={!!isReadOnly}
                        >
                            Re-include part
                        </MenuItem>
                    ) : null}
                </MenuList>
            </Portal>
        </Menu>
    );
}

/** First column: row actions menu (add above/below, optional duplicate, delete, optional re-include) — shared by line/freight/misc/ignored grids. */
export function InvoicePartTableRowActionsCell({
    isReadOnly,
    onAddAbove,
    onAddBelow,
    onDelete,
    onDuplicate,
    onRestore,
    menuTriggerIcon,
}: {
    isReadOnly?: boolean;
    onAddAbove: () => void;
    onAddBelow: () => void;
    onDelete: () => void;
    onDuplicate?: () => void;
    onRestore?: () => void;
    menuTriggerIcon?: React.ElementType;
}) {
    return (
        <Box
            position="absolute"
            left="0"
            top="50%"
            transform="translate(-50%, -50%)"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.15s"
            zIndex={20}
            sx={{ '&:has([aria-expanded="true"])': { opacity: 1 } }}
        >
            <InvoiceRowActionsMenu
                isReadOnly={!!isReadOnly}
                onAddAbove={onAddAbove}
                onAdd={onAddBelow}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onRestore={onRestore}
                {...(menuTriggerIcon ? { menuTriggerIcon } : {})}
            />
        </Box>
    );
}

interface InvoiceMatchedNexToggleButtonProps {
    isExpanded: boolean;
    onClick: () => void;
}

export function InvoiceMatchedNexToggleButton({ isExpanded, onClick }: InvoiceMatchedNexToggleButtonProps) {
    return (
        <Tooltip label={isExpanded ? 'Hide matched NEX line' : 'Show matched NEX line'} placement="top" hasArrow>
            <IconButton
                aria-label={isExpanded ? 'Hide matched NEX line' : 'Show matched NEX line'}
                icon={<Icon as={(isExpanded ? TbChevronDown : TbChevronRight) as React.ElementType} boxSize={4} />}
                size="xs"
                variant="ghost"
                color="blue.600"
                _hover={{ bg: 'blue.50' }}
                minW="auto"
                h="auto"
                p={0}
                flexShrink={0}
                onClick={onClick}
                tabIndex={-1}
            />
        </Tooltip>
    );
}

interface InvoiceIssueFlagButtonProps {
    tooltip: React.ReactNode;
    ariaLabel?: string;
}

export function InvoiceIssueFlagButton({ tooltip, ariaLabel = 'Issue' }: InvoiceIssueFlagButtonProps) {
    return (
        <Tooltip label={tooltip} placement="top" hasArrow>
            <IconButton
                aria-label={ariaLabel}
                icon={<Icon as={TbFlag as React.ElementType} boxSize={4} />}
                variant="ghost"
                size="xs"
                color="orange.500"
                _hover={{ bg: 'transparent' }}
                cursor="pointer"
                minW="auto"
                h="auto"
                p={0}
                flexShrink={0}
                tabIndex={-1}
            />
        </Tooltip>
    );
}

interface InvoiceSuggestionBulbButtonProps {
    tooltip: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    ariaLabel?: string;
}

export function InvoiceSuggestionBulbButton({
    tooltip,
    onClick,
    disabled,
    ariaLabel = 'Suggestion',
}: InvoiceSuggestionBulbButtonProps) {
    return (
        <Tooltip label={tooltip} placement="top" hasArrow>
            {disabled ? (
                <Box as="span" display="inline-flex">
                    <IconButton
                        aria-label={ariaLabel}
                        icon={<Icon as={TbBulb as React.ElementType} boxSize={4} />}
                        variant="ghost"
                        size="xs"
                        color="orange.500"
                        _hover={{ bg: 'transparent' }}
                        cursor="not-allowed"
                        minW="auto"
                        h="auto"
                        p={0}
                        flexShrink={0}
                        isDisabled
                        tabIndex={-1}
                    />
                </Box>
            ) : (
                <IconButton
                    aria-label={ariaLabel}
                    icon={<Icon as={TbBulb as React.ElementType} boxSize={4} />}
                    variant="ghost"
                    size="xs"
                    color="orange.500"
                    _hover={{ bg: 'orange.100' }}
                    cursor="pointer"
                    minW="auto"
                    h="auto"
                    p={0}
                    flexShrink={0}
                    onClick={onClick}
                    tabIndex={-1}
                />
            )}
        </Tooltip>
    );
}

interface InvoiceMatchNoteInfoButtonProps {
    onClick: () => void;
    tooltip?: string;
}

export function InvoiceMatchNoteInfoButton({ onClick, tooltip = "Key reconciliation match note" }: InvoiceMatchNoteInfoButtonProps) {
    return (
        <Tooltip label={tooltip} placement="top" hasArrow>
            <IconButton
                aria-label="Match note"
                icon={<Icon as={TbInfoCircle as React.ElementType} boxSize={4} />}
                variant="ghost"
                size="xs"
                color="blue.600"
                _hover={{ bg: 'blue.50' }}
                minW="auto"
                h="auto"
                p={0}
                flexShrink={0}
                onClick={onClick}
                tabIndex={-1}
            />
        </Tooltip>
    );
}

interface InvoiceMatchedNexRowsProps {
    isExpanded: boolean;
    matchedNex?: {
        partDesc?: string;
        partNumber?: string;
        listPrice?: number;
        unitCost?: number;
    };
    nexQty: number;
    nexTotal: number;
    nexMatchedVendorName?: string;
    nexMatchedPoLineLabel?: string | null;
    nexMatchedStatus?: string | null;
    isReadOnly?: boolean;
    onUnmatch?: () => void;
    formatMoney: (value: number, precision?: number) => string;
    sourceBadge?: React.ReactNode;
    /** Matches parent invoice grid vertical separators (see `getInvoiceItemsTableSx`). */
    showTableGridlines?: boolean;
}

export function InvoiceMatchedNexRows({
    isExpanded,
    matchedNex,
    nexQty,
    nexTotal,
    nexMatchedVendorName,
    nexMatchedPoLineLabel,
    nexMatchedStatus,
    isReadOnly,
    onUnmatch,
    formatMoney,
    sourceBadge,
    showTableGridlines = true,
}: InvoiceMatchedNexRowsProps) {
    const descColumnDividerColor = showTableGridlines ? 'gray.200' : 'transparent';
    const matchedNexGridlineColor = showTableGridlines ? 'gray.200' : 'transparent';
    return (
        <>
            {isExpanded ? (
                <Tr
                    sx={{
                        ...invoiceMatchedNexDetailDataRowTrSx,
                        '& > td + td': {
                            borderLeft: '1px solid',
                            borderLeftColor: matchedNexGridlineColor,
                        },
                    }}
                >
                    <Td py={1.5} border="none" w="116px" minW="116px" px={2} bg="blue.50" />
                    <Td py={1.5} fontSize="sm" textAlign="left" border="none" bg="blue.50">
                        <HStack spacing={1} align="center" wrap="nowrap">
                            <Box flex="1" minW={0}>{(matchedNex?.partDesc ?? '').trim() || '—'}</Box>
                            <Box w="16px" minW="16px" flexShrink={0} />
                        </HStack>
                    </Td>
                    <Td py={1.5} fontSize="sm" textAlign="left" border="none" bg="blue.50">
                        <HStack spacing={1} align="center" wrap="nowrap">
                            <Box flex="1" minW={0}>{matchedNex?.partNumber ?? '—'}</Box>
                            <Box w="16px" minW="16px" flexShrink={0} />
                        </HStack>
                    </Td>
                    <Td py={1.5} fontSize="sm" textAlign="right" border="none" bg="blue.50">
                        <HStack spacing={0} align="center" justify="flex-end">
                            <Box w="100%" textAlign="right" px={0}>{nexQty}</Box>
                            <Box w="16px" minW="16px" flexShrink={0} />
                        </HStack>
                    </Td>
                    <Td py={1.5} fontSize="sm" textAlign="right" border="none" bg="blue.50">
                        <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap">
                            <Box w="100%" textAlign="right">
                                {matchedNex?.listPrice != null
                                    ? matchedNex.listPrice < 0
                                        ? `-$${formatMoney(Math.abs(matchedNex.listPrice))}`
                                        : `$${formatMoney(matchedNex.listPrice)}`
                                    : '—'}
                            </Box>
                        </HStack>
                    </Td>
                    <Td py={1.5} fontSize="sm" textAlign="right" border="none" bg="blue.50">
                        <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap">
                            <Box w="100%" textAlign="right">
                                {matchedNex?.unitCost != null
                                    ? matchedNex.unitCost < 0
                                        ? `-$${formatMoney(Math.abs(matchedNex.unitCost))}`
                                        : `$${formatMoney(matchedNex.unitCost)}`
                                    : '—'}
                            </Box>
                        </HStack>
                    </Td>
                    <Td py={1.5} fontSize="sm" textAlign="right" fontWeight="medium" border="none" bg="blue.50">
                        <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap">
                            <Box w="100%" textAlign="right">
                                {nexTotal < 0 ? `-$${formatMoney(Math.abs(nexTotal))}` : `$${formatMoney(nexTotal)}`}
                            </Box>
                        </HStack>
                    </Td>
                </Tr>
            ) : null}
            <Tr>
                <Td colSpan={7} py={0} px={0} border="none" verticalAlign="top" sx={invoiceMatchedNexCollapseHeaderTdSx}>
                    <Collapse in={isExpanded} animateOpacity>
                        <Flex w="100%" align="stretch" gap={0}>
                            <Flex {...invoiceMatchedNexActionsColumnFlexProps}>
                                <HStack {...invoicePartTableActionsIconStackProps}>
                                    {!isReadOnly && onUnmatch ? (
                                        <Tooltip label="Unmatch NEX line" placement="top" hasArrow>
                                            <IconButton
                                                aria-label="Unmatch NEX line"
                                                icon={<Icon as={TbUnlink as React.ElementType} boxSize={4} />}
                                                size="xs"
                                                variant="ghost"
                                                color="gray.500"
                                                _hover={{ bg: 'red.50', color: 'red.600' }}
                                                minW="auto"
                                                h="auto"
                                                p={0}
                                                flexShrink={0}
                                                onClick={onUnmatch}
                                                tabIndex={-1}
                                            />
                                        </Tooltip>
                                    ) : null}
                                </HStack>
                            </Flex>
                                <Flex
                                    flex="1"
                                    minW={0}
                                    bg="blue.50"
                                    align="center"
                                    justify="space-between"
                                    gap={2}
                                    flexWrap="wrap"
                                    borderLeft="1px solid"
                                    borderLeftColor={descColumnDividerColor}
                                    py={2}
                                    pl={4}
                                    pr={4}
                                >
                                    <HStack spacing={2} align="center" flex="1" minW={0}>
                                        <Text fontSize="xs" fontWeight="semibold" color="blue.700" as="span">
                                            Matched NEX Line Information
                                            {nexMatchedVendorName ? (
                                                <Text as="span" fontWeight="normal" color="blue.600" ml={2}>
                                                    {nexMatchedVendorName}
                                                </Text>
                                            ) : null}
                                        </Text>
                                        {sourceBadge}
                                    </HStack>
                                    {nexMatchedPoLineLabel || nexMatchedStatus ? (
                                        <HStack spacing={2} flexShrink={0}>
                                            {nexMatchedPoLineLabel ? (
                                                <Text fontSize="xs" fontWeight="normal" color="blue.600" whiteSpace="nowrap">
                                                    {nexMatchedPoLineLabel}
                                                </Text>
                                            ) : null}
                                            {nexMatchedStatus ? (
                                                <Tag size="sm" colorScheme={getLineStatusTagColor(nexMatchedStatus)} variant="subtle" whiteSpace="nowrap">
                                                    {getLineStatusDisplayText(nexMatchedStatus)}
                                                </Tag>
                                            ) : null}
                                        </HStack>
                                    ) : null}
                                </Flex>
                            </Flex>
                    </Collapse>
                </Td>
            </Tr>
        </>
    );
}
